import type { Vec2Like } from '../utils/DirectionalMath';

export interface NavObstacle {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

interface Cell { x: number; z: number }

export class GridNavigator {
  private readonly blocked = new Set<string>();

  constructor(
    readonly width: number,
    readonly depth: number,
    readonly cellSize: number,
    obstacles: NavObstacle[],
    clearance = 0.65,
  ) {
    for (let z = 0; z < this.rows; z += 1) {
      for (let x = 0; x < this.columns; x += 1) {
        const world = this.toWorld({ x, z });
        if (obstacles.some((o) => world.x >= o.minX - clearance && world.x <= o.maxX + clearance
          && world.z >= o.minZ - clearance && world.z <= o.maxZ + clearance)) {
          this.blocked.add(this.key({ x, z }));
        }
      }
    }
  }

  get columns(): number { return Math.ceil(this.width / this.cellSize); }
  get rows(): number { return Math.ceil(this.depth / this.cellSize); }

  findPath(start: Vec2Like, goal: Vec2Like): Vec2Like[] {
    const origin = this.closestWalkable(this.toCell(start));
    const target = this.closestWalkable(this.toCell(goal));
    if (!origin || !target) return [];

    const open: Cell[] = [origin];
    const cameFrom = new Map<string, Cell>();
    const g = new Map<string, number>([[this.key(origin), 0]]);
    const f = new Map<string, number>([[this.key(origin), this.heuristic(origin, target)]]);

    while (open.length > 0) {
      open.sort((a, b) => (f.get(this.key(a)) ?? Infinity) - (f.get(this.key(b)) ?? Infinity));
      const current = open.shift();
      if (!current) break;
      if (current.x === target.x && current.z === target.z) return this.reconstruct(cameFrom, current);

      for (const neighbor of this.neighbors(current)) {
        const tentative = (g.get(this.key(current)) ?? Infinity) + this.heuristic(current, neighbor);
        if (tentative >= (g.get(this.key(neighbor)) ?? Infinity)) continue;
        cameFrom.set(this.key(neighbor), current);
        g.set(this.key(neighbor), tentative);
        f.set(this.key(neighbor), tentative + this.heuristic(neighbor, target));
        if (!open.some((c) => c.x === neighbor.x && c.z === neighbor.z)) open.push(neighbor);
      }
    }
    return [];
  }

  private reconstruct(cameFrom: Map<string, Cell>, end: Cell): Vec2Like[] {
    const cells = [end];
    let current = end;
    while (cameFrom.has(this.key(current))) {
      current = cameFrom.get(this.key(current))!;
      cells.unshift(current);
    }
    const points = cells.map((c) => this.toWorld(c));
    return points.filter((point, index) => index === 0 || index === points.length - 1
      || !this.isCollinear(points[index - 1]!, point, points[index + 1]!));
  }

  private isCollinear(a: Vec2Like, b: Vec2Like, c: Vec2Like): boolean {
    return Math.abs((b.x - a.x) * (c.z - b.z) - (b.z - a.z) * (c.x - b.x)) < 0.001;
  }

  private neighbors(cell: Cell): Cell[] {
    const directions = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
    const result: Cell[] = [];
    for (const [dx, dz] of directions) {
      const neighbor = { x: cell.x + dx!, z: cell.z + dz! };
      if (!this.inBounds(neighbor) || this.blocked.has(this.key(neighbor))) continue;
      if (dx && dz) {
        if (this.blocked.has(this.key({ x: cell.x + dx, z: cell.z }))
          || this.blocked.has(this.key({ x: cell.x, z: cell.z + dz }))) continue;
      }
      result.push(neighbor);
    }
    return result;
  }

  private closestWalkable(cell: Cell): Cell | null {
    if (this.inBounds(cell) && !this.blocked.has(this.key(cell))) return cell;
    for (let radius = 1; radius < 6; radius += 1) {
      for (let z = cell.z - radius; z <= cell.z + radius; z += 1) {
        for (let x = cell.x - radius; x <= cell.x + radius; x += 1) {
          const candidate = { x, z };
          if (this.inBounds(candidate) && !this.blocked.has(this.key(candidate))) return candidate;
        }
      }
    }
    return null;
  }

  private toCell(point: Vec2Like): Cell {
    return {
      x: Math.max(0, Math.min(this.columns - 1, Math.floor((point.x + this.width / 2) / this.cellSize))),
      z: Math.max(0, Math.min(this.rows - 1, Math.floor((point.z + this.depth / 2) / this.cellSize))),
    };
  }

  private toWorld(cell: Cell): Vec2Like {
    return {
      x: -this.width / 2 + (cell.x + 0.5) * this.cellSize,
      z: -this.depth / 2 + (cell.z + 0.5) * this.cellSize,
    };
  }

  private heuristic(a: Cell, b: Cell): number { return Math.hypot(a.x - b.x, a.z - b.z); }
  private key(cell: Cell): string { return `${cell.x},${cell.z}`; }
  private inBounds(cell: Cell): boolean { return cell.x >= 0 && cell.z >= 0 && cell.x < this.columns && cell.z < this.rows; }
}
